import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { StyledPageTitle } from 'theme/styles';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { Box, Grid } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';
import FormIntelligenceReport, { IInitialValues } from '../FormIntelligenceReport';
import { Alert } from 'components/atoms/Alert';
import { useCase } from 'pages/CaseManagementPage/CasesPage/hooks';
import { useQuery } from 'hooks/useQuery';
import { isPathEqualToRouteName } from 'utils/route';
import { IReportResultFormValue } from 'models/cases';

const EditIntelligenceReport = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const type: string | null = query.get('type')?.trim() || null;
  const {
    id = '',
    caseId = '',
    reportId = '',
  } = useParams<{
    id: string;
    caseId: string;
    reportId: string;
  }>();
  const { t } = useTranslations();
  const { clearAlert, samePageAlert } = useAlert();
  const { getReportData, report, updateReport } = useCase();
  const { pathname }: any = useLocation();
  const isFromLE = isPathEqualToRouteName(
    pathname,
    ERouteLinks.LegalEntityCaseIntelligenceReportEditPage,
  );
  const breadcrumbs = getBreadcrumbsBasic(isFromLE, type, t, false, id);
  const [initialValues, setInitialValues] = useState<IInitialValues>();

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (reportId) {
      getReportData(reportId);
    }
  }, [reportId]);

  useEffect(() => {
    if (report) {
      setInitialValues({
        trace: report.trace,
        conclusions: report.conclusions,
        deletedAttachmentsUniqueIds: [],
        attachments: [],
        reportAttachments: report?.attachments ?? [],
      });
    }
  }, [report]);

  const submitForm = async (data: IReportResultFormValue) => {
    const res = await updateReport(reportId, data);
    if (res) {
      isFromLE
        ? navigate(
            `${ERouteLinks.LegalEntityCaseIntelligenceReportPage.replace(':caseId', caseId)
              .replace(':id', id)
              .replace(':reportId', reportId)}?type=${type}`,
          )
        : navigate(
            ERouteLinks.CaseIntelligenceReportPage.replace(':caseId', caseId).replace(
              ':reportId',
              reportId,
            ),
          );
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          ...breadcrumbs,
          {
            label: t(Elabels.caseDetailsHeader),
            route: isFromLE
              ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
              : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
          },
          t(Elabels.editIntelligenceReport),
        ]}
      />
      <Grid sx={{ alignItems: 'center' }}>
        <StyledPageTitle variant="h4">{t(Elabels.editIntelligenceReport)}</StyledPageTitle>
      </Grid>
      {samePageAlert && (
        <Box sx={{ mt: 2, mb: 2 }}>
          <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
        </Box>
      )}
      <FormIntelligenceReport
        isEdit
        onSubmit={submitForm}
        initialValues={initialValues}
        prevLink={
          isFromLE
            ? `${ERouteLinks.LegalEntityCaseIntelligenceReportPage.replace(':caseId', caseId)
                .replace(':id', id)
                .replace(':reportId', reportId)}?type=${type}`
            : ERouteLinks.CaseIntelligenceReportPage.replace(':caseId', caseId).replace(
                ':reportId',
                reportId,
              )
        }
      />
    </>
  );
};

export default EditIntelligenceReport;
